import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/lib/auth'
import { Layout } from '@/components/Layout'
import { ForcePasswordChange, SignIn } from '@/pages/SignIn'
import { Overview } from '@/pages/Overview'
import { Worklists } from '@/pages/Worklists'
import { Rules } from '@/pages/Rules'
import { Devices } from '@/pages/Devices'
import { Facilities } from '@/pages/Facilities'
import { Sources } from '@/pages/Sources'
import { Listeners } from '@/pages/Listeners'
import { Orders } from '@/pages/Orders'
import { QueryLog } from '@/pages/QueryLog'
import { Simulator } from '@/pages/Simulator'
import { Users, UsersDenied } from '@/pages/Users'

export function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  )
}

/**
 * Nothing renders until we know who is signed in.
 *
 * The router is not mounted at all for a signed-out browser, so there is no
 * route that could be reached by typing a URL — and no flash of a page the
 * viewer is not entitled to while a request is in flight.
 */
function Gate() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="signin-page">
        <div className="signin-card">
          <div className="brand">
            <span className="brand-mark">MWL</span>
            <span className="brand-sub">Worklist Console</span>
          </div>
          <p className="hint">Checking your session…</p>
        </div>
      </div>
    )
  }

  if (!user) return <SignIn />
  if (user.must_change_password) return <ForcePasswordChange />
  return <Console />
}

function Console() {
  const { can } = useAuth()
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Overview />} />
          <Route path="worklists" element={<Worklists />} />
          <Route path="rules" element={<Rules />} />
          <Route path="simulator" element={<Simulator />} />
          <Route path="devices" element={<Devices />} />
          <Route path="facilities" element={<Facilities />} />
          <Route path="listeners" element={<Listeners />} />
          <Route path="sources" element={<Sources />} />
          <Route path="orders" element={<Orders />} />
          <Route path="query-log" element={<QueryLog />} />
          <Route path="users" element={can('security_admin') ? <Users /> : <UsersDenied />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

function NotFound() {
  return (
    <div className="notice">
      <h1>Not found</h1>
      <p>That page does not exist in the worklist console.</p>
    </div>
  )
}
